import { Tooltip } from '@/components/ui/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { cn } from '@/lib/cn';
import type { MiniMonthCell } from '@/lib/weekCalendar';

export type RailModuleSection = 'tasks' | 'events' | 'notes' | 'holidays' | 'org-events' | 'pms';

type Props = {
  miniMonth: MiniMonthCell[];
  monthAnchor: Date;
  selectedDate: Date | null;
  collapsed: boolean;
  mobileOpen: boolean;
  onCreate: () => void;
  onToggleCollapse: () => void;
  onCloseMobile: () => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDate: (date: Date) => void;

  onPrevYear: () => void;
  onNextYear: () => void;
  onSelectYear: (year: number) => void;
  activeSection: RailModuleSection | null;
  onOpenModule: (section: RailModuleSection) => void;
};

export function CalendarLeftRail({
  miniMonth,
  monthAnchor,
  selectedDate,
  collapsed,
  mobileOpen,
  onCreate,
  onToggleCollapse,
  onCloseMobile,
  onPrevMonth,
  onNextMonth,
  onSelectDate,
  onPrevYear,
  onNextYear,
  onSelectYear,
  activeSection,
  onOpenModule,
}: Props) {
  // Keep the clicked menu highlighted even after its panel closes; only another menu click moves it.
  const [selected, setSelected] = useState<RailModuleSection | null>(activeSection);
  useEffect(() => {
    if (activeSection) setSelected(activeSection);
  }, [activeSection]);
  const openModule = (section: RailModuleSection) => {
    setSelected(section);
    onOpenModule(section);
  };
  const anchorYear = monthAnchor.getFullYear();

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={onCloseMobile}
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-full flex-col border-r bg-card transition-all duration-300 ease-in-out lg:relative lg:z-[45] lg:translate-x-0',
          collapsed ? 'w-[84px]' : 'w-[280px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className={cn('flex items-center', collapsed ? 'justify-center p-3' : 'gap-3 px-5 pb-3 pt-5 lg:px-6')}>
          <img
            src="/calendar-logo.png"
            alt="Sculpx Calendar logo"
            className={cn('shrink-0 object-contain', collapsed ? 'h-12 w-12' : 'h-12 w-12')}
          />
          {!collapsed ? (
            <h2 className="min-w-0 truncate text-lg font-bold tracking-tight text-[#0f172b] dark:text-white">Sculpx Calendar</h2>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="group/toggle absolute -right-3.5 top-[78px] z-50 hidden h-7 w-7 items-center justify-center text-slate-500 transition-all duration-200 hover:scale-110 hover:text-[#5B47E8] active:scale-95 dark:text-slate-400 dark:hover:text-[#8B7CFF] lg:flex"
        >
          <svg className={cn('h-5 w-5 transition-transform duration-300', collapsed ? 'rotate-180 group-hover/toggle:translate-x-0.5' : 'group-hover/toggle:-translate-x-0.5')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
          </svg>
        </button>

        <div className={cn('flex items-center gap-2', collapsed ? 'justify-center p-3' : 'px-4 py-3 sm:px-5 lg:px-6')}>
          <Tooltip label="Create" disabled={!collapsed} className={collapsed ? '' : 'w-full'}>
          <button
            type="button"
            onClick={onCreate}
            className={cn(
              'flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] py-3 text-white shadow-lg shadow-[#6D5CF6]/40 transition hover:opacity-95 hover:shadow-xl active:scale-95',
              collapsed ? 'h-11 w-11 p-0' : 'w-full'
            )}
            aria-label="Create"
          >
            <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            {!collapsed ? <span className="font-semibold tracking-wide">Create</span> : null}
          </button>
          </Tooltip>
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close mobile sidebar"
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary lg:hidden"
          >
            <CloseIcon />
          </button>
        </div>

        {/* <div className={cn('border-b', collapsed ? 'p-3' : 'px-4 pb-3 pt-0 sm:px-5 lg:px-6')}>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="hidden w-full items-center justify-center rounded-lg border border-surface-muted px-2 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-secondary lg:flex"
          >
            {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          </button>
        </div> */}

        <div className="custom-scrollbar flex-1 space-y-6 overflow-y-auto px-3 pb-6 sm:px-4 lg:space-y-8 lg:px-4 lg:pb-8">
          {!collapsed ? (
            <section className="pt-2">
              <div className="rounded-3xl border border-slate-200/70 bg-gradient-to-b from-white to-slate-50 p-4 dark:border-white/5 dark:from-[#12172b] dark:to-[#0e1324]">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Calendar</p>
                    <p className="mt-0.5 truncate text-lg font-bold leading-tight tracking-tight text-[#0f172b] dark:text-white">
                      {format(monthAnchor, 'MMMM')}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center rounded-full bg-slate-100 p-0.5 dark:bg-white/5">
                    <button onClick={onPrevYear} aria-label="Previous year" className="flex h-5 w-5 items-center justify-center rounded-full text-slate-500 transition hover:text-[#5B47E8] dark:text-slate-400 dark:hover:text-[#8B7CFF]"><ChevronLeft /></button>
                    <YearPicker year={anchorYear} onSelect={onSelectYear} />
                    <button onClick={onNextYear} aria-label="Next year" className="flex h-5 w-5 items-center justify-center rounded-full text-slate-500 transition hover:text-[#5B47E8] dark:text-slate-400 dark:hover:text-[#8B7CFF]"><ChevronRight /></button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-400">{format(monthAnchor, 'MMMM yyyy')}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={onPrevMonth} aria-label="Previous month" className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-[#5B6CF6] hover:text-[#5B47E8] dark:border-white/10 dark:text-slate-400 dark:hover:border-[#8B7CFF] dark:hover:text-[#8B7CFF]"><ChevronLeft /></button>
                    <button onClick={onNextMonth} aria-label="Next month" className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-[#5B6CF6] hover:text-[#5B47E8] dark:border-white/10 dark:text-slate-400 dark:hover:border-[#8B7CFF] dark:hover:text-[#8B7CFF]"><ChevronRight /></button>
                  </div>
                </div>

                <div className="mb-1 mt-3 grid grid-cols-7 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((x, i) => (
                    <span key={i}>{x}</span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-y-1">
                  {miniMonth.map((cell, idx) => {
                    const isSelected = selectedDate && selectedDate.toDateString() === cell.date.toDateString();
                    const col = idx % 7;
                    return (
                      <div
                        key={cell.date.toISOString()}
                        className={cn(
                          'flex items-center justify-center py-px',
                          cell.isInActiveWeek && 'bg-[#EEF2FF] dark:bg-[#8B7CFF]/10',
                          cell.isInActiveWeek && col === 0 && 'rounded-l-full',
                          cell.isInActiveWeek && col === 6 && 'rounded-r-full'
                        )}
                      >
                        <button
                          onClick={() => onSelectDate(cell.date)}
                          className={cn(
                            'relative flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-all duration-200',
                            isSelected
                              ? 'bg-gradient-to-br from-[#5B6CF6] to-[#8B5CF6] font-bold text-white'
                              : cell.isToday
                                ? 'font-bold text-[#5B47E8] ring-2 ring-inset ring-[#5B6CF6]/60 dark:text-[#8B7CFF] dark:ring-[#8B7CFF]/60'
                                : !cell.inCurrentMonth
                                  ? 'text-slate-300 hover:bg-slate-100 dark:text-slate-600 dark:hover:bg-white/5'
                                  : cell.isInActiveWeek
                                    ? 'text-[#4338CA] hover:bg-white dark:text-[#B4A9FF] dark:hover:bg-white/10'
                                    : 'text-slate-700 hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:text-slate-200 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]'
                          )}
                        >
                          {format(cell.date, 'd')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          ) : null}

          <section className="space-y-3 pt-2">
            {!collapsed ? <h3 className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Views</h3> : null}
            <nav className="space-y-1.5">
              <RailItem
                label="Events"
                collapsed={collapsed}
                active={selected === 'events'}
                onClick={() => openModule('events')}
                icon={<EventIcon />}
              />
              <RailItem
                label="Notes"
                collapsed={collapsed}
                active={selected === 'notes'}
                onClick={() => openModule('notes')}
                icon={<NoteIcon />}
              />
            </nav>
          </section>

          <section className="space-y-3">
            {!collapsed ? <h3 className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Organization</h3> : null}
            <nav className="space-y-1.5">
              <RailItem
                label="Org Events"
                collapsed={collapsed}
                active={selected === 'org-events'}
                onClick={() => openModule('org-events')}
                icon={<OrgIcon />}
              />
              <RailItem
                label="Holidays"
                collapsed={collapsed}
                active={selected === 'holidays'}
                onClick={() => openModule('holidays')}
                icon={<HolidayIcon />}
              />
            </nav>
          </section>

          <section className="space-y-3">
            {!collapsed ? <h3 className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Integrations</h3> : null}
            <nav className="space-y-1.5">
              <RailItem
                label="Projects & Tasks"
                collapsed={collapsed}
                active={selected === 'pms'}
                onClick={() => openModule('pms')}
                icon={<PmsIcon />}
              />
            </nav>
          </section>
        </div>
      </aside>
    </>
  );
}

function RailItem({
  label,
  collapsed,
  active,
  onClick,
  icon,
}: {
  label: string;
  collapsed: boolean;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [tip, setTip] = useState<{ top: number; left: number } | null>(null);
  const showTip = () => {
    if (!collapsed || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setTip({ top: r.top + r.height / 2, left: r.right + 16 });
  };
  const hideTip = () => setTip(null);

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onFocus={showTip}
      onBlur={hideTip}
      className={cn(
        'group relative flex w-full items-center rounded-full px-4 py-3 text-[15px] font-semibold transition-colors',
        collapsed ? 'mx-auto h-12 w-12 justify-center p-0' : 'gap-4',
        active ? 'bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]' : 'text-[#334155] hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:text-slate-300 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]'
      )}
      aria-label={label}
    >
      <span className={cn(active ? 'text-[#5B47E8] dark:text-[#8B7CFF]' : 'text-slate-500 group-hover:text-[#5B47E8] dark:text-slate-400 dark:group-hover:text-[#8B7CFF]')}>
        {icon}
      </span>
      {!collapsed ? label : null}
      {active ? (
        <span aria-hidden="true" className={cn('absolute top-1/2 h-8 w-1 -translate-y-1/2 rounded-l-full bg-[#5B47E8] dark:bg-[#8B7CFF]', collapsed ? '-right-[18px] sm:-right-[22px]' : '-right-3 sm:-right-4')} />
      ) : null}
      {collapsed && tip
        ? createPortal(
            <span
              role="tooltip"
              style={{ top: tip.top, left: tip.left }}
              className="pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded-xl bg-[#0f172b] px-4 py-2 text-sm font-semibold text-white shadow-lg dark:bg-[#1e293b]"
            >
              <span aria-hidden="true" className="absolute -left-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rotate-45 rounded-[2px] bg-[#0f172b] dark:bg-[#1e293b]" />
              {label}
            </span>,
            document.body
          )
        : null}
    </button>
  );
}

// Minimal Icons
function EventIcon() { return <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>; }
function NoteIcon() { return <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>; }
function OrgIcon() { return <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>; }
function HolidayIcon() { return <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2l2.39 4.84 5.34.78-3.86 3.77.91 5.32L12 14.27 7.22 16.7l.91-5.32L4.27 7.62l5.34-.78L12 2z" /></svg>; }
function PmsIcon() { return <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>; }
function CloseIcon() { return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>; }

function ChevronLeft() {
  return <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M12.5 4.5L7.5 10l5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function ChevronRight() {
  return <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7.5 4.5L12.5 10l-5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

const YEARS_PER_PAGE = 12;

function YearPicker({ year, onSelect }: { year: number; onSelect: (year: number) => void }) {
  const [open, setOpen] = useState(false);
  const [pageStart, setPageStart] = useState(() => year - (year % YEARS_PER_PAGE));
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setPageStart(year - (year % YEARS_PER_PAGE));
  }, [open, year]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-bold text-[#0f172b] transition-colors hover:text-[#5B47E8] dark:text-white dark:hover:text-[#8B7CFF]"
      >
        {year}
        <svg className={cn('h-3 w-3 text-slate-400 transition-transform', open && 'rotate-180')} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 8l5 5 5-5" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-30 mt-2 w-52 rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_20px_50px_-20px_rgba(15,23,43,0.3)] animate-in fade-in slide-in-from-top-1 duration-150 dark:border-white/10 dark:bg-card">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPageStart((p) => p - YEARS_PER_PAGE)}
              aria-label="Earlier years"
              className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 transition hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:text-slate-400 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]"
            >
              <ChevronLeft />
            </button>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {years[0]} – {years[years.length - 1]}
            </span>
            <button
              type="button"
              onClick={() => setPageStart((p) => p + YEARS_PER_PAGE)}
              aria-label="Later years"
              className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 transition hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:text-slate-400 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]"
            >
              <ChevronRight />
            </button>
          </div>
          <div role="listbox" className="grid grid-cols-3 gap-1">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                role="option"
                aria-selected={y === year}
                onClick={() => {
                  onSelect(y);
                  setOpen(false);
                }}
                className={cn(
                  'rounded-xl py-2 text-xs font-semibold transition-colors',
                  y === year
                    ? 'bg-gradient-to-br from-[#5B6CF6] to-[#8B5CF6] text-white'
                    : 'text-slate-600 hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:text-slate-300 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]'
                )}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
